import { formatCurrencyWhole } from '../../utils/formatCurrency.js';

const money = (value) => formatCurrencyWhole(value);

/**
 * Flat commission / remittance ledger — no nested cards.
 * Extra GC-breakdown / returnToCompany props are optional; settlement pages omit them.
 */
export default function AgentCommissionLedger({
  totalFreight,
  freightLabel = 'Total freight',
  gcTotal,
  toPay,
  toPayFace,
  toPayHeld,
  toPayLabel,
  returnToCompany,
  commissionAmount,
  commissionPct,
  commissionStatus,
  /** When true, commission is payable separately (not deducted from remittance). */
  commissionSeparate = false,
  tdsAmount,
  tdsPct,
  netToAgent,
  netDue,
  netDueLabel,
  outstanding,
  shipmentCount,
  loadingKept,
  unloadingKept,
  remitBreakdown,
  note,
  positiveTone = 'rose',
}) {
  const hasCommission = Number(commissionAmount || 0) > 0;
  const hasTds = Number(tdsAmount || 0) > 0;
  const showReturnTotal = returnToCompany != null && returnToCompany !== '';
  const rows = [];

  const breakdown = Array.isArray(remitBreakdown) ? remitBreakdown : [];
  breakdown.forEach((row) => {
    rows.push({
      label: row.label,
      value: money(row.value),
      muted: Boolean(row.muted),
      hint: row.hint || null,
    });
  });

  if (gcTotal != null && gcTotal !== '') {
    rows.push({ label: 'GC totals (incl. GST)', value: money(gcTotal), muted: true });
  }
  if (totalFreight != null && totalFreight !== '') {
    rows.push({ label: freightLabel, value: money(totalFreight), muted: showReturnTotal });
  }
  if (toPayFace != null && toPayFace !== '') {
    rows.push({ label: toPayLabel || 'To-Pay on GCs', value: money(toPayFace) });
  } else if (toPay != null && toPay !== '') {
    rows.push({ label: toPayLabel || 'To-Pay', value: money(toPay) });
  }
  if (!showReturnTotal && Number(toPayHeld || 0) > 0.009) {
    rows.push({
      label: 'After delivery',
      value: money(toPayHeld),
      muted: true,
    });
  }
  if (Number(loadingKept || 0) > 0) {
    rows.push({
      label: breakdown.length ? 'Loading kept' : 'Loading',
      value: money(loadingKept),
      muted: true,
    });
  }
  if (Number(unloadingKept || 0) > 0) {
    rows.push({
      label: breakdown.length ? 'Unloading kept' : 'Unloading',
      value: `− ${money(unloadingKept)}`,
      muted: true,
    });
  }
  if (hasCommission) {
    if (commissionSeparate) {
      rows.push({
        label: `Commission (${commissionPct ?? 0}%)`,
        value: money(commissionAmount),
        muted: true,
        hint: commissionStatus
          ? (String(commissionStatus).toUpperCase() === 'PAID' ? 'Paid separately' : 'Pay separately')
          : 'Pay separately',
      });
    } else {
      rows.push({
        label: `Commission (${commissionPct ?? 0}%)`,
        value: `− ${money(commissionAmount)}`,
        muted: true,
        hint: commissionStatus
          ? (String(commissionStatus).toUpperCase() === 'PAID' ? 'Paid' : 'Unpaid')
          : null,
      });
    }
  }
  if (hasTds) {
    if (commissionSeparate) {
      rows.push({
        label: `TDS (${tdsPct ?? 0}%)`,
        value: money(tdsAmount),
        muted: true,
        hint: 'On commission payout',
      });
      rows.push({
        label: 'Net commission payable',
        value: money(
          netToAgent != null
            ? netToAgent
            : Number(commissionAmount || 0) - Number(tdsAmount || 0),
        ),
        muted: true,
      });
    } else {
      rows.push({
        label: `TDS (${tdsPct ?? 0}%)`,
        value: `− ${money(tdsAmount)}`,
        muted: true,
      });
      rows.push({
        label: 'Net to agent',
        value: money(
          netToAgent != null
            ? netToAgent
            : Number(commissionAmount || 0) - Number(tdsAmount || 0),
        ),
        muted: true,
      });
    }
  }

  const remitLabel = netDueLabel
    || (commissionSeparate
      ? 'To-Pay − unloading kept'
      : (hasTds ? 'Net to remit (To-Pay − net commission)' : 'Net to remit (To-Pay − commission)'));

  return (
    <div className="text-sm">
      {showReturnTotal && (
        <div className="flex items-center justify-between gap-4 pb-2 mb-2 border-b-2 border-gray-200">
          <span className="font-semibold text-gray-900 min-w-0">Return to company</span>
          <span
            className={`tabular-nums whitespace-nowrap font-semibold ${
              Number(returnToCompany) > 0
                ? (positiveTone === 'emerald' ? 'text-emerald-700' : 'text-rose-700')
                : 'text-emerald-700'
            }`}
          >
            {money(returnToCompany)}
          </span>
        </div>
      )}
      {rows.map((row) => (
        <div
          key={row.label}
          className={`flex items-center justify-between gap-4 py-1 border-b border-gray-100 ${
            row.muted ? 'text-gray-500' : 'text-gray-700'
          }`}
        >
          <span className="min-w-0">
            {row.label}
            {row.hint ? (
              <span className="ml-1.5 text-[10px] font-medium text-gray-400">{row.hint}</span>
            ) : null}
          </span>
          <span className="tabular-nums whitespace-nowrap font-medium text-gray-900">{row.value}</span>
        </div>
      ))}
      {(!showReturnTotal && netDue != null && netDue !== '') && (
        <div className="flex items-center justify-between gap-4 pt-2">
          <span className="font-semibold text-gray-800 min-w-0">{remitLabel}</span>
          <span
            className={`tabular-nums whitespace-nowrap font-semibold ${
              Number(netDue) > 0
                ? (positiveTone === 'emerald' ? 'text-emerald-700' : 'text-rose-700')
                : 'text-emerald-700'
            }`}
          >
            {money(netDue)}
          </span>
        </div>
      )}
      {(!showReturnTotal && outstanding != null && outstanding !== '') && (
        <div className="flex items-center justify-between gap-4 pt-1.5 text-gray-600">
          <span>Outstanding</span>
          <span
            className={`tabular-nums font-semibold ${
              Number(outstanding) > 0 ? 'text-rose-700' : 'text-emerald-700'
            }`}
          >
            {money(outstanding)}
          </span>
        </div>
      )}
      {shipmentCount != null && (
        <div className="flex items-center justify-between gap-4 pt-1 text-gray-500">
          <span>Shipments</span>
          <span className="tabular-nums">{shipmentCount}</span>
        </div>
      )}
      {(!showReturnTotal && note) ? (
        <p className="text-[11px] text-gray-400 leading-relaxed pt-2">{note}</p>
      ) : null}
    </div>
  );
}
